import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { DocumentViewerMetaProps } from '../data/types'
import { Card } from './ui'
import { LoadingSpinner } from './LoadingSpinner'
import { ChevronLeftIcon, ChevronRightIcon, CompressIcon, DocumentIcon, ExpandIcon, RotateIcon, ZoomInIcon, ZoomOutIcon } from '../icons'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

// Check if native fullscreen API is supported (not available on iOS Safari)
const isFullscreenSupported = () => {
    return document.fullscreenEnabled ||
        (document as any).webkitFullscreenEnabled ||
        (document as any).mozFullScreenEnabled ||
        (document as any).msFullscreenEnabled
}

type Props = {
    fileUrl: string
    title: string
    meta: DocumentViewerMetaProps
    isGuideline?: boolean
    onContentCompleted?: () => void
}

export const DocumentViewer = (props: Props) => {
    const { fileUrl, title, meta, onContentCompleted, isGuideline = false } = props
    const [numPages, setNumPages] = useState<number>(0)
    const [pageNumber, setPageNumber] = useState<number>(1)
    const [rotation, setRotation] = useState<number>(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isPdf, setIsPdf] = useState(false)
    const [hasReachedLastPage, setHasReachedLastPage] = useState(false)
    const [hasTriggeredCompletion, setHasTriggeredCompletion] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [containerWidth, setContainerWidth] = useState<number>(0)
    const [currentScale, setCurrentScale] = useState<number>(1)
    const [showTapZones, setShowTapZones] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [displayedPage, setDisplayedPage] = useState<number>(1)
    const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set())
    // For iOS: simulated fullscreen when native API not available
    const [isSimulatedFullscreen, setIsSimulatedFullscreen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const fullscreenContainerRef = useRef<HTMLDivElement>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const transformRef = useRef<ReactZoomPanPinchRef>(null)
    const tapZoneTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Helper to show tap zones with resettable timeout
    const flashTapZones = useCallback((duration: number = 1000) => {
        if (tapZoneTimeoutRef.current) {
            clearTimeout(tapZoneTimeoutRef.current)
        }
        setShowTapZones(true)
        tapZoneTimeoutRef.current = setTimeout(() => setShowTapZones(false), duration)
    }, [])

    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 640)
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    // Detect container width for responsive scaling
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth)
            }
        }

        updateWidth()
        window.addEventListener('resize', updateWidth)
        return () => window.removeEventListener('resize', updateWidth)
    }, [])

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isNowFullscreen = !!document.fullscreenElement
            setIsFullscreen(isNowFullscreen)
            // Show tap zones briefly when entering fullscreen
            if (isNowFullscreen) {
                flashTapZones(2000)
            } else if (isMobile) {
                // Reset transform when exiting fullscreen on mobile so preview looks clean
                setTimeout(() => {
                    transformRef.current?.resetTransform()
                    setCurrentScale(0.5)
                }, 50)
            }
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        // Also listen for webkit prefix (Safari on macOS)
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        }
    }, [flashTapZones, isMobile])

    // Lock body scroll when in simulated fullscreen (iOS)
    useEffect(() => {
        if (isSimulatedFullscreen) {
            // Prevent body scrolling on iOS
            document.body.style.overflow = 'hidden'
            document.body.style.position = 'fixed'
            document.body.style.width = '100%'
            document.body.style.height = '100%'
            // Show tap zones briefly
            flashTapZones(2000)
        } else {
            document.body.style.overflow = ''
            document.body.style.position = ''
            document.body.style.width = ''
            document.body.style.height = ''
        }
        return () => {
            document.body.style.overflow = ''
            document.body.style.position = ''
            document.body.style.width = ''
            document.body.style.height = ''
        }
    }, [isSimulatedFullscreen, flashTapZones])

    useEffect(() => {
        setLoading(true)
        setError(null)
        setHasTriggeredCompletion(false)
        setHasReachedLastPage(false)
        setPageNumber(1)
        setDisplayedPage(1)
        setRenderedPages(new Set())
        setCurrentScale(0.5)
        setRotation(0)
        // Reset zoom transform when URL changes
        transformRef.current?.resetTransform()

        // Check if URL is valid
        if (!fileUrl) {
            setError(meta.errorInvalidUrl)
            setLoading(false)
            return
        }

        // Detect if the file is a PDF based on URL or file extension
        const urlLower = fileUrl.toLowerCase()
        const isPdfFile = urlLower.endsWith('.pdf') || urlLower.includes('.pdf?')
        setIsPdf(isPdfFile)

        if (!isPdfFile) {
            // For non-PDF files, simulate loading completion after iframe loads
            const timer = setTimeout(() => setLoading(false), 1000)
            return () => clearTimeout(timer)
        }
    }, [fileUrl, meta.errorInvalidUrl])

    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages)
        setLoading(false)
        // If it's a single-page document, user is already on the last page
        if (numPages === 1) {
            setHasReachedLastPage(true)
        }
        // Show tap zones briefly on mobile when document loads
        if (numPages > 1 && window.innerWidth < 640) {
            flashTapZones(2000)
        }
    }, [flashTapZones])

    const onDocumentLoadError = useCallback(() => {
        setError(meta.errorLoadingDocument)
        setLoading(false)
    }, [meta.errorLoadingDocument])

    // Handle page render completion - update displayed page when target is ready
    const onPageRenderSuccess = useCallback((pageNum: number) => {
        setRenderedPages(prev => new Set(prev).add(pageNum))
    }, [])

    // Update displayed page when target page is rendered
    useEffect(() => {
        if (renderedPages.has(pageNumber)) {
            setDisplayedPage(pageNumber)
        }
    }, [pageNumber, renderedPages])

    const goToPrevPage = () => {
        const newPage = Math.max(pageNumber - 1, 1)
        // If page is already rendered, switch immediately
        if (renderedPages.has(newPage)) {
            setDisplayedPage(newPage)
        }
        setPageNumber(newPage)
        // Flash tap zones briefly
        flashTapZones(1000)
    }

    const goToNextPage = () => {
        const newPage = Math.min(pageNumber + 1, numPages)
        if (newPage === numPages) {
            setHasReachedLastPage(true)
        }
        // If page is already rendered, switch immediately
        if (renderedPages.has(newPage)) {
            setDisplayedPage(newPage)
        }
        setPageNumber(newPage)
        // Flash tap zones briefly
        flashTapZones(1000)
    }

    const zoomIn = () => {
        transformRef.current?.zoomIn(0.25)
    }

    const zoomOut = () => {
        transformRef.current?.zoomOut(0.25)
    }

    const rotate = () => {
        setRotation(prev => (prev + 90) % 360)
    }

    const resetZoom = () => {
        transformRef.current?.resetTransform()
        setCurrentScale(0.5)
    }

    const handleTransform = (ref: ReactZoomPanPinchRef) => {
        setCurrentScale(ref.state.scale)
    }

    const toggleFullscreen = async () => {
        // Check if native fullscreen is supported
        if (isFullscreenSupported()) {
            // Use native fullscreen API
            if (!fullscreenContainerRef.current) return

            if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
                try {
                    if (fullscreenContainerRef.current.requestFullscreen) {
                        await fullscreenContainerRef.current.requestFullscreen()
                    } else if ((fullscreenContainerRef.current as any).webkitRequestFullscreen) {
                        await (fullscreenContainerRef.current as any).webkitRequestFullscreen()
                    }
                } catch (err) {
                    // Fullscreen request failed, fall back to simulated
                    setIsSimulatedFullscreen(true)
                    setIsFullscreen(true)
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen()
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen()
                }
            }
        } else {
            // iOS Safari: Use simulated fullscreen (fixed position overlay)
            if (isSimulatedFullscreen) {
                setIsSimulatedFullscreen(false)
                setIsFullscreen(false)
                // Reset transform for clean preview
                setTimeout(() => {
                    transformRef.current?.resetTransform()
                    setCurrentScale(0.5)
                }, 50)
            } else {
                setIsSimulatedFullscreen(true)
                setIsFullscreen(true)
            }
        }
    }

    // Calculate optimal page width for the PDF - render at 2x for zoom headroom
    const getPageWidth = useCallback(() => {
        const isMobile = window.innerWidth < 640

        let baseWidth: number
        if (isFullscreen) {
            baseWidth = window.innerWidth - 64
        } else if (isMobile) {
            baseWidth = window.innerWidth - 48
        } else {
            const availableWidth = containerWidth > 0 ? containerWidth - 48 : 700
            baseWidth = Math.min(availableWidth, 750)
        }

        // Render at 2x for crisp zooming up to 200%
        return baseWidth * 2
    }, [containerWidth, isFullscreen])

    const handleMarkAsRead = async () => {
        if (!hasTriggeredCompletion && onContentCompleted) {
            // Exit fullscreen first so any subsequent modals appear correctly
            if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
                if (document.exitFullscreen) {
                    await document.exitFullscreen()
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen()
                }
            }
            // Also exit simulated fullscreen (iOS)
            if (isSimulatedFullscreen) {
                setIsSimulatedFullscreen(false)
                setIsFullscreen(false)
            }
            setHasTriggeredCompletion(true)
            onContentCompleted()
        }
    }

    if (error) {
        return (
            <Card>
                <div className="p-8 text-center">
                    <DocumentIcon className="w-12 h-12 mx-auto mb-4 text-danger-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {meta.errorTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{error}</p>
                </div>
            </Card>
        )
    }

    // For non-PDF files, use iframe embed
    if (!isPdf) {
        return (
            <>
                <Card className='dark:bg-white'>
                    <div className="relative" style={{ height: '70vh', minHeight: '400px', maxHeight: '700px' }}>
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800 z-10">
                                <LoadingSpinner meta={{ loadingText: meta.loadingText }} />
                            </div>
                        )}
                        <iframe
                            ref={iframeRef}
                            src={fileUrl}
                            title={title}
                            className="w-full h-full border-0"
                            onLoad={() => setLoading(false)}
                            onError={() => {
                                setError(meta.errorLoadingDocument)
                                setLoading(false)
                            }}
                            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        />
                    </div>
                </Card>

                {!hasTriggeredCompletion && (
                    <Card className="mt-4 border-2 border-primary-500 dark:border-primary-400">
                        <div className="p-4 flex items-center justify-between gap-4">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                    Finished reviewing the document?
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Click the button to indicate you've reviewed this content
                                </p>
                            </div>
                            <button
                                onClick={handleMarkAsRead}
                                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                            >
                                I've Read This
                            </button>
                        </div>
                    </Card>
                )}
            </>
        )
    }

    // Icon color classes based on fullscreen state
    const iconColorClass = isFullscreen
        ? 'text-gray-200'
        : 'text-gray-700 dark:text-gray-300'

    const buttonHoverClass = isFullscreen
        ? 'hover:bg-gray-700'
        : 'hover:bg-gray-300 dark:hover:bg-gray-700'

    // Mobile preview mode - constrained view with "View Document" button
    const showMobilePreview = isMobile && !isFullscreen

    // Completion prompt should show:
    // - On desktop: below the viewer (mt-4)
    // - On fullscreen: overlay at bottom
    // - On mobile preview: below the preview card
    // But NOT inside the viewer when in mobile preview mode
    const showCompletionPrompt = !hasTriggeredCompletion && hasReachedLastPage && !showMobilePreview && !isGuideline;

    return (
        <>
            {/* Main viewer container */}
            <div
                ref={fullscreenContainerRef}
                className={isFullscreen ? 'fixed inset-0 bg-gray-900 flex flex-col z-50' : 'relative'}
                style={isFullscreen ? {
                    // Handle iOS safe areas (notch, home indicator)
                    paddingTop: 'env(safe-area-inset-top)',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    paddingLeft: 'env(safe-area-inset-left)',
                    paddingRight: 'env(safe-area-inset-right)'
                } : { contain: 'inline-size' }}
            >
                <Card className={`${isFullscreen ? 'flex-1 rounded-none border-0 flex flex-col' : ''} ${showMobilePreview ? 'overflow-hidden' : ''}`}>
                    {/* Toolbar - hidden on mobile preview */}
                    {!showMobilePreview && (
                        <div className={`flex items-center justify-between gap-1 p-2 sm:p-3 border-b ${isFullscreen ? 'bg-gray-800 border-gray-700' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 shadow-sm'}`}>
                            {/* Left side - Zoom controls (desktop only, pinch-to-zoom on mobile) */}
                            <div className="hidden sm:flex items-center gap-1 sm:w-[140px]">
                                <button
                                    onClick={zoomOut}
                                    disabled={currentScale <= 0.25}
                                    className={`p-2 rounded-lg ${buttonHoverClass} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
                                    aria-label="Zoom out"
                                >
                                    <ZoomOutIcon className={`w-5 h-5 ${iconColorClass}`} />
                                </button>
                                <button
                                    onClick={resetZoom}
                                    className={`px-2 py-1 text-sm font-medium ${buttonHoverClass} rounded-lg transition-colors min-w-[50px] ${isFullscreen ? 'text-gray-200' : 'text-gray-700 dark:text-gray-300'}`}
                                    aria-label="Reset zoom"
                                >
                                    {Math.round(currentScale * 200)}%
                                </button>
                                <button
                                    onClick={zoomIn}
                                    disabled={currentScale >= 1.5}
                                    className={`p-2 rounded-lg ${buttonHoverClass} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
                                    aria-label="Zoom in"
                                >
                                    <ZoomInIcon className={`w-5 h-5 ${iconColorClass}`} />
                                </button>
                            </div>

                            {/* Center - Page Navigation (always visible) */}
                            <div className="flex-1 flex items-center sm:justify-center gap-1 sm:gap-2">
                                <button
                                    onClick={goToPrevPage}
                                    disabled={pageNumber <= 1}
                                    className={`p-2 rounded-lg ${buttonHoverClass} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
                                    aria-label="Previous page"
                                >
                                    <ChevronLeftIcon className={`w-5 h-5 ${iconColorClass}`} />
                                </button>
                                <span className={`text-sm font-medium min-w-[50px] sm:min-w-[70px] text-center ${isFullscreen ? 'text-gray-200' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {pageNumber}/{numPages || '?'}
                                </span>
                                <button
                                    onClick={goToNextPage}
                                    disabled={pageNumber >= numPages}
                                    className={`p-2 rounded-lg ${buttonHoverClass} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
                                    aria-label="Next page"
                                >
                                    <ChevronRightIcon className={`w-5 h-5 ${iconColorClass}`} />
                                </button>
                            </div>

                            {/* Right side - Additional controls */}
                            <div className="flex items-center justify-end gap-1 sm:w-[140px]">
                                <button
                                    onClick={rotate}
                                    className={`hidden sm:block p-2 rounded-lg ${buttonHoverClass} transition-colors`}
                                    aria-label="Rotate"
                                >
                                    <RotateIcon className={`w-5 h-5 ${iconColorClass}`} />
                                </button>
                                <button
                                    onClick={toggleFullscreen}
                                    className={`p-2 rounded-lg ${buttonHoverClass} transition-colors`}
                                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                >
                                    {isFullscreen ? (
                                        <CompressIcon className={`w-5 h-5 ${iconColorClass}`} />
                                    ) : (
                                        <ExpandIcon className={`w-5 h-5 ${iconColorClass}`} />
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* PDF Content - Scrollable container with zoom/pan support */}
                    <div
                        ref={containerRef}
                        className={`relative ${isFullscreen ? 'flex-1' : ''} ${isFullscreen ? 'bg-gray-900' : 'bg-gray-200 dark:bg-gray-800'}`}
                        style={{
                            height: isFullscreen ? undefined : (showMobilePreview ? '200px' : 'calc(100vh - 280px)'),
                            minHeight: showMobilePreview ? undefined : '300px',
                            maxHeight: isFullscreen ? undefined : (showMobilePreview ? '200px' : '600px'),
                            overflow: 'hidden'
                        }}
                    >
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center z-10">
                                <LoadingSpinner meta={{ loadingText: meta.loadingText }} />
                            </div>
                        )}
                        <TransformWrapper
                            ref={transformRef}
                            initialScale={0.5}
                            minScale={0.25}
                            maxScale={1.5}
                            centerOnInit={true}
                            onTransformed={handleTransform}
                            doubleClick={{ disabled: showMobilePreview, mode: 'toggle' }}
                            panning={{ disabled: showMobilePreview, velocityDisabled: false }}
                            pinch={{ disabled: showMobilePreview }}
                            wheel={{ disabled: true }}
                        >
                            <TransformComponent
                                wrapperStyle={{
                                    width: '100%',
                                    height: '100%',
                                    cursor: showMobilePreview ? 'default' : 'grab'
                                }}
                                contentStyle={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    padding: showMobilePreview ? '32px' : '16px'
                                }}
                            >
                                <Document
                                    file={fileUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={null}
                                >
                                    {/* Render buffered pages (prev, current, next) for smooth transitions */}
                                    {showMobilePreview ? (
                                        <Page
                                            pageNumber={1}
                                            width={(window.innerWidth - 64) * 2}
                                            rotate={rotation}
                                            className="shadow-xl rounded-xl overflow-hidden"
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            loading={null}
                                        />
                                    ) : (
                                        <div className="relative">
                                            {/* Pre-render adjacent pages (hidden) */}
                                            {[pageNumber - 1, pageNumber, pageNumber + 1]
                                                .filter(p => p >= 1 && p <= numPages)
                                                .map(p => (
                                                    <div
                                                        key={p}
                                                        className={p === displayedPage ? 'block' : 'absolute invisible pointer-events-none'}
                                                        style={p !== displayedPage ? { position: 'absolute', left: '-9999px' } : undefined}
                                                    >
                                                        <Page
                                                            pageNumber={p}
                                                            width={getPageWidth()}
                                                            rotate={rotation}
                                                            className="shadow-xl rounded-xl overflow-hidden"
                                                            renderTextLayer={false}
                                                            renderAnnotationLayer={true}
                                                            loading={null}
                                                            onRenderSuccess={() => onPageRenderSuccess(p)}
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </Document>
                            </TransformComponent>
                        </TransformWrapper>

                        {/* Mobile tap zones for page navigation - only in fullscreen */}
                        {isFullscreen && numPages > 1 && (
                            <>
                                {/* Left tap zone - previous page */}
                                {pageNumber > 1 && (
                                    <button
                                        onClick={goToPrevPage}
                                        className={`sm:hidden absolute left-0 top-0 bottom-0 w-16 z-20 transition-opacity duration-500 ${showTapZones ? 'opacity-100' : 'opacity-0'}`}
                                        aria-label="Previous page"
                                        style={{ touchAction: 'manipulation' }}
                                    >
                                        <div className="h-full flex items-center justify-center bg-gradient-to-r from-black/30 to-transparent">
                                            <ChevronLeftIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                        </div>
                                    </button>
                                )}
                                {/* Right tap zone - next page */}
                                {pageNumber < numPages && (
                                    <button
                                        onClick={goToNextPage}
                                        className={`sm:hidden absolute right-0 top-0 bottom-0 w-16 z-20 transition-opacity duration-500 ${showTapZones ? 'opacity-100' : 'opacity-0'}`}
                                        aria-label="Next page"
                                        style={{ touchAction: 'manipulation' }}
                                    >
                                        <div className="h-full flex items-center justify-center bg-gradient-to-l from-black/30 to-transparent">
                                            <ChevronRightIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                        </div>
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Mobile preview footer - info and button */}
                    {showMobilePreview && (
                        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {title}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {numPages > 0 ? `${numPages} page${numPages > 1 ? 's' : ''}` : 'PDF Document'}
                                    </p>
                                </div>
                                <button
                                    onClick={toggleFullscreen}
                                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <ExpandIcon className="w-4 h-4" />
                                    View
                                </button>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Completion Prompt - inside container for fullscreen positioning */}
                {showCompletionPrompt && (
                    <div className={isFullscreen ? 'absolute bottom-4 left-4 right-4 z-30' : 'mt-4'}>
                        <Card className={`border-2 border-primary-500 dark:border-primary-400 ${isFullscreen ? '!bg-gray-800/95 backdrop-blur-sm' : ''}`}>
                            <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                                <div className="flex-1">
                                    <p className={`text-sm font-medium mb-1 ${isFullscreen ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                                        Finished reviewing the document?
                                    </p>
                                    <p className={`text-xs ${isFullscreen ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                                        Click the button to indicate you've reviewed this content
                                    </p>
                                </div>
                                <button
                                    onClick={handleMarkAsRead}
                                    className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                                >
                                    I've Read This
                                </button>
                            </div>
                        </Card>
                    </div>
                )}
            </div>

            {/* Mobile completion prompt - outside container, below preview */}
            {showMobilePreview && !hasTriggeredCompletion && hasReachedLastPage && (
                <Card className="mt-4 border-2 border-primary-500 dark:border-primary-400">
                    <div className="p-4 flex flex-col items-start justify-between gap-3">
                        <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                Finished reviewing the document?
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                Tap the button to indicate you've reviewed this content
                            </p>
                        </div>
                        <button
                            onClick={handleMarkAsRead}
                            className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            I've Read This
                        </button>
                    </div>
                </Card>
            )}
        </>
    )
}
